import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import {
  ColumnDef,
  RowSelectionState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Checkbox } from 'components/ui/checkbox';
import { DataTable } from 'components/ui/data-table';
import { Input } from 'components/ui/input';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Column } from 'interfaces/column';
import React, { useEffect, useMemo, useState } from 'react';

type DatasetColumnPickerProps = {
  data: Column[];
  onChange: (selectedColumns: Column[]) => void;
};

const DatasetColumnPicker = React.forwardRef<
  HTMLDivElement,
  DatasetColumnPickerProps
>(({ data, onChange }, ref) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedGeoCode, setSelectedGeoCode] = useState<string | null>(null);
  const [searchKeywords, setSearchKeywords] = useState<string>('');

  const filteredRows = useMemo(() => {
    return searchKeywords
      ? data.filter((row) => {
          return row.name.includes(searchKeywords);
        })
      : data;
  }, [data, searchKeywords]);

  const selectedColumnNames = useMemo(
    () => Object.keys(rowSelection),
    [rowSelection]
  );

  useEffect(() => {
    if (selectedGeoCode && !selectedColumnNames.includes(selectedGeoCode)) {
      setSelectedGeoCode(null);
    }
  }, [selectedGeoCode, selectedColumnNames]);

  useEffect(() => {
    const selectedNameSet = new Set(selectedColumnNames);
    const selected = data
      .filter((row) => selectedNameSet.has(row.name))
      .map(
        (column) =>
          ({
            ...column,
            isGeoCode: column.name === selectedGeoCode,
          } as Column)
      );
    onChange(selected);
  }, [data, onChange, selectedColumnNames, selectedGeoCode]);

  const columns = useMemo<ColumnDef<Column>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                ? 'indeterminate'
                : false
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Sélectionner toutes les colonnes"
            className="mx-auto"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={`Sélectionner ${row.original.name}`}
            className="mx-auto"
          />
        ),
        enableSorting: false,
        enableHiding: false,
        meta: {
          className: 'w-12 text-center',
          cellClassName: 'text-center',
        },
      },
      {
        id: 'geoCode',
        header: 'GéoCode',
        cell: ({ row }) => {
          const isSelected = row.getIsSelected();
          return (
            <RadioGroupItem
              value={row.original.name}
              aria-label={`Select ${row.original.name} as geo code`}
              className="mx-auto"
              disabled={!isSelected}
              onClick={(event) => {
                event.stopPropagation();
                if (!isSelected) {
                  row.toggleSelected(true);
                }
              }}
            />
          );
        },
        enableSorting: false,
        enableHiding: false,
        meta: {
          className: 'w-20 text-center',
          cellClassName: 'text-center',
        },
      },
      {
        accessorKey: 'name',
        header: 'Colonne',
      },
      {
        accessorKey: 'sample',
        header: 'Échantillon',
      },
      {
        accessorKey: 'source',
        header: 'Source',
      },
      {
        accessorKey: 'validity',
        header: 'Validité',
      },
    ],
    []
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => row.name,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="border-slate-300" ref={ref}>
      <div className="relative mb-2">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
        <Input
          id="keywords"
          className="w-full pl-9"
          value={searchKeywords}
          onChange={(e) => setSearchKeywords(e.target.value)}
          autoComplete={'false'}
        />
      </div>
      <RadioGroup
        value={selectedGeoCode ?? undefined}
        onValueChange={setSelectedGeoCode}
        className="flex flex-col"
      >
        <DataTable table={table} className="max-h-96 overflow-y-auto" />
      </RadioGroup>
    </div>
  );
});

DatasetColumnPicker.displayName = 'DatasetColumnPicker';

export default DatasetColumnPicker;
